
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../Context/LanguageContext";
import "./Gallery.css";

const EVENTS_API_URL =
    "https://script.google.com/macros/s/AKfycbwoNQHZnU5j4Pb8NIwzuVsBg5TBKlKRhYJXsxmCK0J6Rb8QRCf6-ixVudn7-KReaDcu/exec";

const IMAGE_BASE_PATH = "/images/events";

function Gallery() {
    const { language } = useLanguage();

    const [events, setEvents] = useState([]);
    const [selectedYear, setSelectedYear] = useState("all");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const isBn = language === "bn";

    /* =========================================
       LOAD EVENTS
    ========================================= */

    useEffect(() => {
        const controller = new AbortController();

        async function loadEvents() {
            try {
                setLoading(true);
                setError("");

                const response = await fetch(EVENTS_API_URL, {
                    signal: controller.signal
                });

                if (!response.ok) {
                    throw new Error("Failed to load gallery.");
                }

                const data = await response.json();

                // Existing API returns upcoming and past events.
                const allEvents = [
                    ...(Array.isArray(data.upcoming) ? data.upcoming : []),
                    ...(Array.isArray(data.past) ? data.past : [])
                ];

                // Keep events that have gallery images.
                const galleryEvents = allEvents.filter((event) => {
                    return (
                        event.id &&
                        String(event.gallery_images || "").trim()
                    );
                });

                // Sort newest first.
                galleryEvents.sort((a, b) =>
                    String(b.date || "").localeCompare(
                        String(a.date || "")
                    )
                );

                setEvents(galleryEvents);
            } catch (err) {
                if (err.name !== "AbortError") {
                    console.error("Gallery error:", err);
                    setError(err.message);
                }
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
        }

        loadEvents();

        return () => controller.abort();
    }, []);

    /* =========================================
       YEARS
    ========================================= */

    const years = useMemo(() => {
        return [
            ...new Set(
                events
                    .map((event) =>
                        String(event.date || "").slice(0, 4)
                    )
                    .filter((year) => /^\d{4}$/.test(year))
            )
        ].sort((a, b) => b.localeCompare(a));
    }, [events]);

    /* =========================================
       FILTER EVENTS
    ========================================= */

    const filteredEvents = useMemo(() => {
        if (selectedYear === "all") {
            return events;
        }

        return events.filter(
            (event) =>
                String(event.date || "").slice(0, 4) === selectedYear
        );
    }, [events, selectedYear]);

    /* =========================================
       IMAGE HELPERS
    ========================================= */

    const getGalleryImages = (event) => {
        return String(event.gallery_images || "")
            .split(",")
            .map((image) => image.trim())
            .filter(Boolean)
            .map((image) => {
                if (/^https?:\/\//i.test(image)) {
                    return image;
                }

                if (image.startsWith("/")) {
                    return image;
                }

                return `${IMAGE_BASE_PATH}/${encodeURIComponent(
                    event.id
                )}/${image}`;
            });
    };

    const getCoverImage = (event) => {
        const images = getGalleryImages(event);

        if (images.length > 0) {
            return images[0];
        }

        return event.image || "";
    };

    const getEventTitle = (event) => {
        return isBn
            ? event.title_bn || event.title_en
            : event.title_en || event.title_bn;
    };

    const formatDate = (date) => {
        if (!date) return "";

        const parsedDate = new Date(`${date}T00:00:00`);

        if (Number.isNaN(parsedDate.getTime())) {
            return date;
        }

        return parsedDate.toLocaleDateString(
            isBn ? "bn-BD" : "en-GB",
            {
                year: "numeric",
                month: "long",
                day: "numeric"
            }
        );
    };

    /* =========================================
       RENDER
    ========================================= */

    return (
        <main className="gallery-page">

            {/* HERO */}

            <section className="gallery-hero">
                <div className="container gallery-hero-content">

                    <span className="gallery-hero-label">
                        {isBn
                            ? "আমাদের স্মৃতি"
                            : "OUR MEMORIES"}
                    </span>

                    <h1>
                        {isBn
                            ? "স্মৃতির অ্যালবাম"
                            : "Our Photo Gallery"}
                    </h1>

                    <p>
                        {isBn
                            ? "আমাদের আনন্দ, উৎসব, খেলাধুলা ও একসাথে কাটানো সুন্দর মুহূর্তগুলোর স্মৃতি।"
                            : "Explore memories from our celebrations, sports, cultural events and community gatherings."}
                    </p>

                </div>
            </section>

            {/* ALBUMS */}

            <section className="gallery-section">
                <div className="container">

                    <div className="gallery-section-heading">

                        <div>
                            <span className="gallery-section-label">
                                {isBn
                                    ? "ছবির সংগ্রহ"
                                    : "PHOTO COLLECTION"}
                            </span>

                            <h2>
                                {isBn
                                    ? "অনুষ্ঠানের অ্যালবাম"
                                    : "Event Albums"}
                            </h2>

                            <p>
                                {isBn
                                    ? "অনুষ্ঠান নির্বাচন করে ছবিগুলো দেখুন।"
                                    : "Select an event to explore its photos."}
                            </p>
                        </div>

                        {!loading && !error && years.length > 0 && (
                            <div className="gallery-filter">
                                <label htmlFor="galleryYear">
                                    {isBn ? "বছর" : "Year"}
                                </label>

                                <select
                                    id="galleryYear"
                                    value={selectedYear}
                                    onChange={(e) =>
                                        setSelectedYear(e.target.value)
                                    }
                                >
                                    <option value="all">
                                        {isBn
                                            ? "সব বছর"
                                            : "All Years"}
                                    </option>

                                    {years.map((year) => (
                                        <option key={year} value={year}>
                                            {year}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                    </div>

                    {/* LOADING */}

                    {loading && (
                        <div className="gallery-status">
                            <div className="gallery-loader"></div>

                            <p>
                                {isBn
                                    ? "অ্যালবাম লোড হচ্ছে..."
                                    : "Loading photo albums..."}
                            </p>
                        </div>
                    )}

                    {/* ERROR */}

                    {!loading && error && (
                        <div className="gallery-status gallery-error">
                            <h3>
                                {isBn
                                    ? "অ্যালবাম লোড করা যায়নি"
                                    : "Unable to load albums"}
                            </h3>

                            <p>{error}</p>
                        </div>
                    )}

                    {/* EMPTY */}

                    {!loading &&
                        !error &&
                        filteredEvents.length === 0 && (
                            <div className="gallery-status">
                                <h3>
                                    {isBn
                                        ? "কোনো অ্যালবাম পাওয়া যায়নি"
                                        : "No albums found"}
                                </h3>

                                <p>
                                    {isBn
                                        ? "এই বছরের কোনো ছবির অ্যালবাম নেই।"
                                        : "There are no photo albums for this selection."}
                                </p>
                            </div>
                        )}

                    {/* ALBUM CARDS */}

                    {!loading &&
                        !error &&
                        filteredEvents.length > 0 && (
                            <div className="gallery-grid">

                                {filteredEvents.map((event) => {
                                    const images = getGalleryImages(event);
                                    const cover = getCoverImage(event);

                                    return (
                                        <Link
                                            key={event.id}
                                            to={`/gallery/${encodeURIComponent(
                                                event.id
                                            )}`}
                                            className="gallery-album-card"
                                        >
                                            <div className="gallery-album-image">

                                                {cover ? (
                                                    <img
                                                        src={cover}
                                                        alt={getEventTitle(event)}
                                                        loading="lazy"
                                                    />
                                                ) : (
                                                    <div className="gallery-no-image">
                                                        {isBn
                                                            ? "ছবি নেই"
                                                            : "No image"}
                                                    </div>
                                                )}

                                                <span className="gallery-photo-count">
                                                    {images.length}{" "}
                                                    {isBn
                                                        ? "টি ছবি"
                                                        : images.length === 1
                                                            ? "Photo"
                                                            : "Photos"}
                                                </span>

                                            </div>

                                            <div className="gallery-album-info">

                                                <span className="gallery-album-date">
                                                    {formatDate(event.date)}
                                                </span>

                                                <h3>
                                                    {getEventTitle(event)}
                                                </h3>

                                                <span className="gallery-album-link">
                                                    {isBn
                                                        ? "অ্যালবাম দেখুন"
                                                        : "View Album"}

                                                    <span aria-hidden="true">
                                                        →
                                                    </span>
                                                </span>

                                            </div>
                                        </Link>
                                    );
                                })}

                            </div>
                        )}

                </div>
            </section>

        </main>
    );
}

export default Gallery;
